import {
  Flex,
  Icon,
  Td,
  Text,
  Tr,
  useColorModeValue,
  useBreakpointValue,
  Box,
  Badge,
  Checkbox,
  IconButton,
  Tooltip,
  Spinner,
  useToast
} from "@chakra-ui/react";
import React, { useState } from "react";
import { FaFileInvoiceDollar, FaCalendar, FaFilePdf, FaReceipt } from "react-icons/fa";
import { formatCurrency } from "services/billing";
import { generateSimpleVoucher } from "services/woo";
import axios from 'axios';
import { getSiteConfig } from 'utils/site';

const UserProfilePaymentTableRow = (props) => {
  const {
    id,
    date_created,
    date_modified,
    total,
    status,
    meta_data = [],
    line_items = [],
    onClick,
    isSelected = false,
    onSelect,
    showCheckbox = false
  } = props;

  const textColor = useColorModeValue("gray.700", "white");
  const isMobile = useBreakpointValue({ base: true, md: false });
  const toast = useToast();
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingVoucher, setLoadingVoucher] = useState(false);
  
  // Obtener billmysales_order_id de meta_data
  const billmysalesOrderId = meta_data.find(meta => meta.key === 'billmysales_order_id')?.value;

  // Extraer datos del primer line item o meta_data
  let concepto = 'Mensualidad';
  let mes = '';
  let cuota = '';

  if (line_items && line_items.length > 0) {
    const firstItem = line_items[0];
    const itemMeta = firstItem.meta_data || [];
    
    concepto = itemMeta.find(meta => meta.key === 'concepto')?.value || 
               itemMeta.find(meta => meta.key === 'glosa')?.value || 
               firstItem.name || 'Mensualidad';
    mes = itemMeta.find(meta => meta.key === 'mes')?.value || '';
    cuota = itemMeta.find(meta => meta.key === 'cuota')?.value || '';
  }

  // Fallback a meta_data de la orden
  if (!concepto || concepto === 'Mensualidad') {
    concepto = meta_data.find(meta => meta.key === 'concepto')?.value || 
               meta_data.find(meta => meta.key === 'glosa')?.value || 'Mensualidad';
  }
  if (!mes) {
    mes = meta_data.find(meta => meta.key === 'mes')?.value || '';
  }
  if (!cuota) {
    cuota = meta_data.find(meta => meta.key === 'cuota')?.value || '';
  }
  
  // Detectar si es Diciembre Cuota 1 (matrícula)
  const isDiciembreCuota1 = mes?.includes('-31') || cuota === 'Cuota 1';

  // Formatear fechas
  const formattedDateCreated = new Date(date_created).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  const formattedTimeCreated = new Date(date_created).toLocaleTimeString('es-CL', {
    hour: '2-digit',
    minute: '2-digit'
  });

  const formattedDateModified = date_modified ? new Date(date_modified).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }) : '-';

  // Formatear mes/año
  const formatMesAno = (mesValue) => {
    if (!mesValue) return '-';
    
    const [year, month] = mesValue.split('-');
    if (!year || !month) return mesValue;
    
    const mesesES = {
      '01': 'Ene', '02': 'Feb', '03': 'Mar', '04': 'Abr',
      '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Ago',
      '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dic'
    };
    
    return `${mesesES[month] || month}/${year.slice(2)}`;
  };

  // Función para manejar click en checkbox (evitar propagación)
  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    // NO usar preventDefault aquí para permitir el comportamiento normal del checkbox
    if (onSelect) {
      onSelect(e.target.checked);
    }
  };
  
  // Manejar click en la celda del checkbox
  const handleCheckboxCellClick = (e) => {
    // Solo detener propagación si NO es click directo en el checkbox
    if (e.target.type !== 'checkbox') {
      e.stopPropagation();
    }
  };
  
  // Función para obtener PDF de BillMySales
  const handleGetBillMySalesPdf = async (e) => {
    e.stopPropagation();
    setLoadingPdf(true);
    try {
      const config = JSON.parse(getSiteConfig());
      const wpApiUrl = config?.configs?.find(c => c.key === 'WP_API_URL')?.value;
      
      if (!wpApiUrl) {
        throw new Error('WP_API_URL no configurado');
      }

      const response = await axios.get(`${wpApiUrl}/datofy/v1/orders/${id}/billmysales-pdf`);
      
      if (response.data.success) {
        // Si hay link PDF, abrirlo automáticamente
        if (response.data.links?.pdf) {
          window.open(response.data.links.pdf, '_blank');
        }
        
        toast({
          title: 'PDF Obtenido',
          description: `Factura #${response.data.invoice_data?.number || 'N/A'} - ${response.data.invoice_data?.document || 'Documento'}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Sin BillMySales ID',
          description: response.data.message || 'Esta orden no tiene BillMySales Order ID asociado',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error obteniendo PDF de BillMySales:', error);
      toast({
        title: 'Error',
        description: 'No se pudo obtener el PDF. Verifique que la orden tenga BillMySales ID.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingPdf(false);
    }
  };

  // Función para obtener comprobante simple
  const handleGetSimpleVoucher = async (e) => {
    e.stopPropagation();
    setLoadingVoucher(true);
    
    try {
      const result = await generateSimpleVoucher(id);
      
      if (result.success) {
        toast({
          title: 'Comprobante Generado',
          description: `Comprobante #${id} generado exitosamente`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Error al Generar Comprobante',
          description: result.message || 'No se pudo generar el comprobante',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error generando comprobante simple:', error);
      toast({
        title: 'Error',
        description: 'Ocurrió un error inesperado al generar el comprobante.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoadingVoucher(false);
    }
  };

  // Badge de estado
  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      'completed': { colorScheme: 'green', label: 'Pagado' },
      'pending': { colorScheme: 'orange', label: 'Pendiente' },
      'processing': { colorScheme: 'blue', label: 'Procesando' },
      'on-hold': { colorScheme: 'yellow', label: 'En espera' },
      'cancelled': { colorScheme: 'red', label: 'Cancelado' },
      'refunded': { colorScheme: 'purple', label: 'Reembolsado' },
      'failed': { colorScheme: 'red', label: 'Fallido' },
      'repactado': { colorScheme: 'teal', label: 'Repactado' }
    };

    const config = statusConfig[status] || { colorScheme: 'gray', label: status };

    return (
      <Badge colorScheme={config.colorScheme} variant="solid" fontSize="xs">
        {config.label}
      </Badge>
    );
  };

  if (isMobile) {
    return (
      <Tr 
        onClick={(e) => onClick && onClick(e)} 
        cursor="pointer" 
        _hover={{ bg: isSelected ? "blue.50" : "gray.50" }}
        bg={isSelected ? "blue.25" : "white"}
      >
        <Td>
          <Flex direction="column" gap={2}>
            {/* Header con checkbox y número de orden */}
            <Flex justify="space-between" align="center">
              <Box>
                <Text fontSize="md" color={textColor} fontWeight="bold">
                  #{id}
                </Text>
                <Text fontSize="sm" color="gray.500" noOfLines={2}>
                  {concepto}
                </Text>
              </Box>
              {/* Checkbox móvil */}
              {showCheckbox && (
                <Box onClick={handleCheckboxCellClick}>
                  <Checkbox
                    isChecked={isSelected}
                    onChange={handleCheckboxClick}
                    colorScheme="blue"
                    size="md"
                  />
                </Box>
              )}
            </Flex>

            {/* Detalles del Pago */}
            <Box>
              <Text fontSize="sm" color="gray.500">
                Creado: {formattedDateCreated} {formattedTimeCreated}
              </Text>
              {mes && (
                <Flex align="center" mt={1}>
                  <Icon as={FaCalendar} h="14px" w="14px" mr={2} color="blue.500" />
                  <Text fontSize="sm" color="blue.500" fontWeight="medium">
                    {formatMesAno(mes)}
                    {isDiciembreCuota1 && (
                      <Badge ml={1} size="xs" colorScheme="purple" variant="solid" fontSize="8px">
                        MAT
                      </Badge>
                    )}
                  </Text>
                </Flex>
              )}
              <Text fontSize="lg" color={textColor} fontWeight="bold" mt={2}>
                {formatCurrency(total)}
              </Text>
            </Box>

            {/* Estado y Boleta (móvil) */}
            <Flex justify="space-between" align="center">
              <Box>
                {getPaymentStatusBadge(status)}
              </Box>
              
              {/* Botones PDF y Comprobante en móvil */}
              <Flex gap={2}>
                {/* Botón Comprobante Simple - Solo para órdenes completadas */}
                {status === 'completed' && (
                  <Tooltip label="Generar Comprobante de Pago" hasArrow>
                    <IconButton
                      icon={loadingVoucher ? <Spinner size="sm" /> : <Icon as={FaReceipt} />}
                      colorScheme="blue"
                      variant="ghost"
                      size="sm"
                      aria-label="Generar Comprobante"
                      onClick={handleGetSimpleVoucher}
                      isLoading={loadingVoucher}
                    />
                  </Tooltip>
                )}
                
                {/* Botón PDF BillMySales */}
                {billmysalesOrderId && (
                  <Tooltip label="Descargar PDF de Factura" hasArrow>
                    <IconButton
                      icon={loadingPdf ? <Spinner size="sm" /> : <Icon as={FaFilePdf} />}
                      colorScheme="teal"
                      variant="ghost"
                      size="sm"
                      aria-label="Descargar PDF"
                      onClick={handleGetBillMySalesPdf}
                      isLoading={loadingPdf}
                    />
                  </Tooltip>
                )}
              </Flex>
            </Flex>
          </Flex>
        </Td>
      </Tr>
    );
  }

  // Vista Desktop - Columnas específicas para perfil de usuario
  return (
    <Tr 
      onClick={(e) => onClick && onClick(e)} 
      cursor="pointer" 
      _hover={{ bg: isSelected ? "blue.50" : "gray.50" }} 
      bg={isSelected ? "blue.25" : "white"}
      height="60px"
    >
      {/* Checkbox de selección */}
      {showCheckbox && (
        <Td width="40px" py={2} onClick={handleCheckboxCellClick}>
          <Checkbox
            isChecked={isSelected}
            onChange={handleCheckboxClick}
            colorScheme="blue"
            size="sm"
          />
        </Td>
      )}

      {/* Estado - Primera columna */}
      <Td width="15%" py={2} textAlign="center">
        {getPaymentStatusBadge(status)}
      </Td>

      {/* Orden */}
      <Td width="20%" py={2}>
        <Text fontSize="sm" color={textColor} fontWeight="bold" lineHeight="1.2">
          #{id}
        </Text>
        <Text fontSize="xs" color="gray.500" mt={1} noOfLines={1} title={concepto}>
          {concepto}
        </Text>
      </Td>

      {/* Mes/Año */}
      <Td width="15%" py={2}>
        {mes ? (
          <Box>
            <Text fontSize="sm" color="blue.600" fontWeight="bold" lineHeight="1.2">
              {formatMesAno(mes)}
              {isDiciembreCuota1 && (
                <Badge ml={1} size="xs" colorScheme="purple" variant="solid" fontSize="8px">
                  MAT
                </Badge>
              )}
            </Text>
            <Text fontSize="xs" color="gray.500" mt={1}>
              {mes}
            </Text>
          </Box>
        ) : (
          <Text fontSize="xs" color="gray.400">-</Text>
        )}
      </Td>

      {/* Creación */}
      <Td width="18%" py={2}>
        <Text fontSize="xs" color={textColor} fontWeight="medium" lineHeight="1.2">
          {formattedDateCreated}
        </Text>
        <Text fontSize="xs" color="gray.500" mt={1}>
          {formattedTimeCreated}
        </Text>
      </Td>

      {/* Actualización */}
      <Td width="17%" py={2}>
        {date_modified ? (
          <Box>
            <Text fontSize="xs" color="orange.600" fontWeight="medium" lineHeight="1.2">
              {formattedDateModified}
            </Text>
          </Box>
        ) : (
          <Text fontSize="xs" color="gray.400">-</Text>
        )}
      </Td>

      {/* Monto */}
      <Td width="13%" py={2} textAlign="right">
        <Text fontSize="sm" color={textColor} fontWeight="bold" lineHeight="1.2">
          {formatCurrency(total)}
        </Text>
      </Td>
      
      {/* Comprobante y Boleta */}
      <Td width="12%" py={2} textAlign="center">
        <Flex justify="center" gap={1}>
          {/* Botón Comprobante Simple - Solo para órdenes completadas */}
          {status === 'completed' && (
            <Tooltip label="Generar Comprobante de Pago" hasArrow>
              <IconButton
                icon={loadingVoucher ? <Spinner size="sm" /> : <Icon as={FaReceipt} />}
                colorScheme="blue"
                variant="ghost"
                size="sm"
                aria-label="Generar Comprobante"
                onClick={handleGetSimpleVoucher}
                isLoading={loadingVoucher}
              />
            </Tooltip>
          )}
          
          {/* Botón PDF BillMySales */}
          {billmysalesOrderId ? (
            <Tooltip label="Descargar PDF de Factura" hasArrow>
              <IconButton
                icon={loadingPdf ? <Spinner size="sm" /> : <Icon as={FaFilePdf} />}
                colorScheme="teal"
                variant="ghost"
                size="sm"
                aria-label="Descargar PDF"
                onClick={handleGetBillMySalesPdf}
                isLoading={loadingPdf}
              />
            </Tooltip>
          ) : (
            <Box width="32px" /> // Espacio vacío para mantener alineación
          )}
        </Flex>
      </Td>
    </Tr>
  );
};

export default UserProfilePaymentTableRow;